import { licenseEnum } from "./licensesEnum.js";
/**
 * The schema was taken from the Unofficial JSON schema for Zenodo upload metadata the 16th of June 2025.
 * https:// github.com/zenodraft/metadata-schema-zenodo
 */
export const zenodoMetadataSchema = {
    $schema: 'http://json-schema.org/draft-07/schema',
    additionalProperties: true,
    allOf: [
        {
            if: {
                type: 'object',
                properties: {
                    access_right: {
                        const: 'embargoed',
                    },
                },
                required: ['access_right'],
            },
            // eslint-disable-next-line unicorn/no-thenable
            then: {
                type: 'object',
                required: ['embargo_date', 'license'],
            },
        },
    ],
    definitions: {},
    properties: {
        access_conditions: {
            type: 'string',
        },
        access_right: {
            description: 'Open access uploads have considerably higher visibility on Zenodo.',
            enum: ['closed', 'embargoed', 'open', 'restricted'],
            type: 'string',
        },
        communities: {
            items: {
                additionalProperties: true,
                properties: {
                    identifier: {
                        type: 'string',
                    },
                },
                required: [],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        conference_acronym: {
            type: 'string',
        },
        conference_dates: {
            type: 'string',
        },
        conference_place: {
            type: 'string',
        },
        conference_session: {
            type: 'string',
        },
        conference_session_part: {
            type: 'string',
        },
        conference_title: {
            type: 'string',
        },
        conference_url: {
            format: 'uri',
            type: 'string',
        },
        contributors: {
            items: {
                additionalProperties: false,
                properties: {
                    affiliation: {
                        minLength: 1,
                        type: ['string', 'null'],
                    },
                    name: {
                        description: 'Family name, given names',
                        minLength: 1,
                        type: 'string',
                    },
                    orcid: {
                        description: 'ORCID identifier',
                        examples: ['0000-0002-1825-0097'],
                        pattern: '[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{3}[0-9X]{1}',
                        type: 'string',
                    },
                    type: {
                        enum: [
                            'ContactPerson',
                            'DataCollector',
                            'DataCurator',
                            'DataManager',
                            'Distributor',
                            'Editor',
                            'HostingInstitution',
                            'Other',
                            'Producer',
                            'ProjectLeader',
                            'ProjectManager',
                            'ProjectMember',
                            'RegistrationAgency',
                            'RegistrationAuthority',
                            'RelatedPerson',
                            'Researcher',
                            'ResearchGroup',
                            'RightsHolder',
                            'Sponsor',
                            'Supervisor',
                            'WorkPackageLeader',
                            null,
                        ],
                        type: ['string', 'null'],
                    },
                },
                required: ['name', 'type'],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        creators: {
            items: {
                additionalProperties: false,
                properties: {
                    affiliation: {
                        minLength: 1,
                        type: ['string', 'null'],
                    },
                    name: {
                        description: 'Family name, given names',
                        minLength: 1,
                        type: 'string',
                    },
                    orcid: {
                        description: 'ORCID identifier',
                        examples: ['0000-0002-1825-0097'],
                        pattern: '[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{3}[0-9X]{1}',
                        type: 'string',
                    },
                },
                required: ['name'],
                type: 'object',
            },
            type: 'array',
            uniqueItems: true,
            minItems: 1,
        },
        description: {
            description: 'Description',
            type: 'string',
        },
        doi: {
            oneOf: [
                {
                    examples: ['10.5281/zenodo.1003150'],
                    pattern: String.raw `^10\.\d{4,9}(\.\d+)?/[A-Za-z0-9:/_;\-\.\(\)\[\]\\]+$`,
                    type: 'string',
                },
                {
                    const: '',
                },
            ],
        },
        embargo_date: {
            format: 'date',
            type: 'string',
        },
        grants: {
            items: {
                additionalProperties: true,
                examples: ['10.13039/501100000780::675191'],
                properties: {
                    id: {
                        type: 'string',
                    },
                },
                required: [],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        image_type: {
            enum: ['diagram', 'drawing', 'figure', 'other', 'photo', 'plot'],
            type: 'string',
        },
        imprint_isbn: {
            type: 'string',
        },
        imprint_place: {
            type: 'string',
        },
        imprint_publisher: {
            type: 'string',
        },
        journal_issue: {
            type: 'string',
        },
        journal_pages: {
            type: 'string',
        },
        journal_title: {
            type: 'string',
        },
        journal_volume: {
            type: 'string',
        },
        keywords: {
            items: {
                type: 'string',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        language: {
            description: 'ISO 639 language identifier.',
            examples: ['ada', 'eng', 'bla'],
            pattern: '^[a-z]{2,3}$',
            type: 'string',
        },
        license: {
            anyOf: [
                licenseEnum,
                { type: 'string' },
                {
                    type: 'object',
                    properties: {
                        id: {
                            anyOf: [licenseEnum, { type: 'string' }],
                        },
                    },
                    required: ['id'],
                    additionalProperties: false,
                },
            ],
        },
        notes: {
            minLength: 0,
            type: 'string',
        },
        partof_pages: {
            type: 'string',
        },
        partof_title: {
            type: 'string',
        },
        publication_date: {
            description: 'Format: YYYY-MM-DD. In case your upload was already published elsewhere, please use the date of first publication.',
            examples: ['2013-05-08'],
            format: 'date',
            type: 'string',
        },
        publication_type: {
            enum: [
                'annotationcollection',
                'article',
                'book',
                'conferencepaper',
                'datamanagementplan',
                'deliverable',
                'milestone',
                'other',
                'patent',
                'preprint',
                'proposal',
                'report',
                'section',
                'softwaredocumentation',
                'taxonomictreatment',
                'technicalnote',
                'thesis',
                'workingpaper',
            ],
            type: 'string',
        },
        references: {
            items: {
                type: 'string',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        related_identifiers: {
            items: {
                additionalProperties: false,
                properties: {
                    identifier: {
                        type: 'string',
                    },
                    relation: {
                        enum: [
                            'cites',
                            'compiles',
                            'continues',
                            'describes',
                            'documents',
                            'hasMetadata',
                            'hasPart',
                            'hasVersion',
                            'isAlternateIdentifier',
                            'isCitedBy',
                            'isCompiledBy',
                            'isContinuedBy',
                            'isDerivedFrom',
                            'isDescribedBy',
                            'isDocumentedBy',
                            'isIdenticalTo',
                            'isMetadataFor',
                            'isNewVersionOf',
                            'isObsoletedBy',
                            'isOriginalFormOf',
                            'isPartOf',
                            'isPreviousVersionOf',
                            'isPublishedIn',
                            'isReferencedBy',
                            'isRequiredBy',
                            'isReviewedBy',
                            'isSourceOf',
                            'isSupplementedBy',
                            'isSupplementTo',
                            'isVariantFormOf',
                            'isVersionOf',
                            'obsoletes',
                            'references',
                            'requires',
                            'reviews',
                        ],
                        type: 'string',
                    },
                    resource_type: {
                        enum: [
                            'dataset',
                            'image-diagram',
                            'image-drawing',
                            'image-figure',
                            'image-other',
                            'image-photo',
                            'image-plot',
                            'lesson',
                            'other',
                            'physicalobject',
                            'poster',
                            'presentation',
                            'publication',
                            'publication-annotationcollection',
                            'publication-article',
                            'publication-book',
                            'publication-conferencepaper',
                            'publication-datamanagementplan',
                            'publication-deliverable',
                            'publication-milestone',
                            'publication-other',
                            'publication-patent',
                            'publication-preprint',
                            'publication-proposal',
                            'publication-report',
                            'publication-section',
                            'publication-softwaredocumentation',
                            'publication-taxonomictreatment',
                            'publication-technicalnote',
                            'publication-thesis',
                            'publication-workingpaper',
                            'software',
                            'video',
                        ],
                        type: 'string',
                    },
                    scheme: {
                        enum: [
                            'ads',
                            'ark',
                            'arxiv',
                            'doi',
                            'ean13',
                            'handle',
                            'isbn',
                            'isni',
                            'issn',
                            'istc',
                            'lsid',
                            'orcid',
                            'pmid',
                            'purl',
                            'url',
                            'urn',
                            'other',
                        ],
                        type: 'string',
                    },
                },
                required: ['identifier', 'relation'],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        resource_type: {
            properties: {
                type: {
                    enum: [
                        'dataset',
                        'image-diagram',
                        'image-drawing',
                        'image-figure',
                        'image-other',
                        'image-photo',
                        'image-plot',
                        'lesson',
                        'other',
                        'physicalobject',
                        'poster',
                        'presentation',
                        'publication',
                        'publication-annotationcollection',
                        'publication-article',
                        'publication-book',
                        'publication-conferencepaper',
                        'publication-datamanagementplan',
                        'publication-deliverable',
                        'publication-milestone',
                        'publication-other',
                        'publication-patent',
                        'publication-preprint',
                        'publication-proposal',
                        'publication-report',
                        'publication-section',
                        'publication-softwaredocumentation',
                        'publication-taxonomictreatment',
                        'publication-technicalnote',
                        'publication-thesis',
                        'publication-workingpaper',
                        'software',
                        'video',
                    ],
                    type: 'string',
                },
                title: {
                    type: 'string',
                },
            },
            type: 'object',
        },
        relations: {
            additionalProperties: false,
            properties: {
                version: {
                    type: 'array',
                    items: {
                        type: 'object',
                        properties: {
                            index: {
                                type: 'integer',
                                minimum: 0,
                            },
                            is_last: {
                                type: 'boolean',
                            },
                            parent: {
                                type: 'object',
                                properties: {
                                    pid_type: {
                                        type: 'string',
                                    },
                                    pid_value: {
                                        type: 'string',
                                    },
                                },
                            },
                        },
                    },
                },
            },
            type: 'object',
        },
        subjects: {
            items: {
                additionalProperties: false,
                properties: {
                    identifier: {
                        type: 'string',
                    },
                    scheme: {
                        enum: [
                            'ads',
                            'ark',
                            'arxiv',
                            'ascl',
                            'bioproject',
                            'biosample',
                            'doi',
                            'ean13',
                            'ean8',
                            'ensembl',
                            'genome',
                            'gnd',
                            'hal',
                            'handle',
                            'isbn',
                            'isni',
                            'issn',
                            'istc',
                            'lsid',
                            'orcid',
                            'pmcid',
                            'pmid',
                            'purl',
                            'refseq',
                            'sra',
                            'swh',
                            'uniprot',
                            'url',
                            'urn',
                        ],
                        type: 'string',
                    },
                    term: {
                        type: 'string',
                    },
                },
                required: ['identifier', 'term'],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        thesis_supervisors: {
            items: {
                additionalProperties: false,
                properties: {
                    affiliation: {
                        minLength: 1,
                        type: ['string', 'null'],
                    },
                    name: {
                        description: 'Family name, given names',
                        minLength: 1,
                        type: 'string',
                    },
                    orcid: {
                        description: 'ORCID identifier',
                        examples: ['0000-0002-1825-0097'],
                        pattern: '[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{3}[0-9X]{1}',
                        type: 'string',
                    },
                },
                required: ['name'],
                type: 'object',
            },
            minItems: 0,
            type: 'array',
            uniqueItems: true,
        },
        thesis_university: {
            type: 'string',
        },
        title: {
            type: 'string',
        },
        upload_type: {
            enum: [
                'dataset',
                'image',
                'lesson',
                'other',
                'physicalobject',
                'poster',
                'presentation',
                'publication',
                'software',
                'video',
            ],
            type: 'string',
        },
        version: {
            type: 'string',
        },
        prereserve_doi: {
            type: 'object',
            required: ['doi', 'recid'],
            properties: {
                doi: { type: 'string' },
                recid: { type: 'integer' },
            },
            additionalProperties: false,
        },
    },
    required: [],
    type: 'object',
};
//# sourceMappingURL=ZenodoMetadataSchema.js.map